/**
 * Confirmação de "Cancelar assinatura" (chamado 7116).
 *
 * Cancelar é só cancelar a assinatura: os recursos Premium seguem até o fim do
 * período contratado (ou do teste) e depois a conta passa ao Plano Gratuito,
 * com os dados salvos. O modal tenta segurar o assinante — lembra o que ele
 * perde, deixa claro até quando mantém os recursos e põe "Manter minha
 * assinatura" como ação principal. O motivo é obrigatório (categoria para o
 * churn por motivo); o comentário, opcional.
 */
import { useEffect, useState } from 'react';
import { Loader2, ShieldCheck } from 'lucide-react';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertDialogCloseButton } from '@/components/ui/alert-dialog-close-button';
import { Button } from '@/components/ui/button';
import {
  CANCEL_BUTTON_CLASS,
  CANCELLATION_AFTER_TEXT,
  CANCELLATION_LOSS_TEXT,
  CANCELLATION_REASONS,
  cancellationKeepText,
  cancellationTitle,
} from '@/lib/subscription-cancellation';
import type { CancellationReason } from '@/services/account.service';

import { ReasonFields } from './ReasonFields';

interface CancelSubscriptionDialogProps {
  open: boolean;
  planName: string;
  /** Até quando os recursos seguem (`AAAA-MM-DD`), vindo do backend. */
  effectiveDate: string;
  inTrial: boolean;
  submitting: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reasonCategory: CancellationReason, reason: string | undefined) => void;
}

export function CancelSubscriptionDialog({
  open,
  planName,
  effectiveDate,
  inTrial,
  submitting,
  onOpenChange,
  onConfirm,
}: Readonly<CancelSubscriptionDialogProps>) {
  const [motivo, setMotivo] = useState<CancellationReason | ''>('');
  const [comentario, setComentario] = useState('');

  // Cada abertura recomeça do zero.
  useEffect(() => {
    if (open) {
      setMotivo('');
      setComentario('');
    }
  }, [open]);

  const confirmar = () => {
    if (!motivo) return;
    onConfirm(motivo, comentario.trim() || undefined);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-xl">
        {/* Fechar sem escolher nada equivale a "Manter minha assinatura". */}
        <AlertDialogCloseButton onClick={() => onOpenChange(false)} disabled={submitting} />
        <AlertDialogHeader>
          <AlertDialogTitle className="px-6 sm:pl-0 sm:pr-8">{cancellationTitle(planName)}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3 text-sm">
              <p>{CANCELLATION_LOSS_TEXT}</p>
              <div className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-left text-emerald-900 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-100">
                <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div className="space-y-1">
                  <p>{cancellationKeepText(effectiveDate, inTrial)}</p>
                  <p>{CANCELLATION_AFTER_TEXT}</p>
                </div>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <ReasonFields<CancellationReason>
          reasonLabel="Qual o principal motivo do cancelamento?"
          options={CANCELLATION_REASONS}
          reason={motivo}
          onReasonChange={setMotivo}
          comment={comentario}
          onCommentChange={setComentario}
        />

        <AlertDialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            className={CANCEL_BUTTON_CLASS}
            onClick={confirmar}
            disabled={submitting || !motivo}
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Confirmar cancelamento
          </Button>
          <AlertDialogCancel
            className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
            disabled={submitting}
          >
            Manter minha assinatura
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default CancelSubscriptionDialog;
